<template>
  <b-modal ref="modal" hide-footer :title="title" size="lg">
    <div v-if="financial">
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(onSubmit)">
          <b-row class="my-1">
            <b-col md="3">
              <label for="month">Month / Year</label>
            </b-col>
            <b-col md="3">
              <validation-provider
                name="Month"
                :rules="{ required: true }"
                v-slot="validationContext"
              >
                <b-form-select
                  id="month"
                  v-model="financial.month"
                  :options="months"
                  value-field="id"
                  text-field="name"
                  :state="getValidationState(validationContext)"
                >
                </b-form-select>
                <b-form-invalid-feedback
                  >{{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-col>
            <b-col md="2">
              <validation-provider
                name="Year"
                :rules="{ required: true }"
                v-slot="validationContext"
              >
                <b-form-select
                  id="year"
                  v-model="financial.year"
                  :options="years"
                  :state="getValidationState(validationContext)"
                >
                </b-form-select>
                <b-form-invalid-feedback
                  >{{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col md="3"></b-col>
            <b-col md="4">
              <b-form-checkbox id="isProjection" v-model="financial.isProjection">Is Projection</b-form-checkbox>
            </b-col>
          </b-row>

           <b-row class="my-1">
            <b-col md="3">
              <label for="turnover">Turnover</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="turnover"
                  v-model="financial.turnoverAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="bankDeposits">Bank Deposits</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="bankDeposits"
                  v-model="financial.bankDepositsAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="grossProfit">Gross Profit</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="grossProfit"
                  v-model="financial.grossProfitAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="grossProfitPercent">Gross Profit Percent</label>
            </b-col>
            <b-col md="4">
              <b-input-group append="%">
                <b-form-input
                  id="grossProfitPercent"
                  v-model="grossProfitPercent"
                  :disabled="true"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="ebit">EBIT</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input id="ebit" v-model="financial.ebitAdjusted"></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="depreciation">Depreciation</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="depreciation"
                  v-model="financial.depreciationAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="amortisation">Amortisation</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="amortisation"
                  v-model="financial.amortisationAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="ebitda">EBITDA</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="ebitda"
                  v-model="ebitda"
                  :disabled="true"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col md="3">
              <label for="shareholdersFunds">Shareholders Funds</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="shareholdersFunds"
                  v-model="financial.shareholdersFundsAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="surplusAssets">Surplus Assets</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="surplusAssets"
                  v-model="financial.surplusAssetsAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="numberOfEmployees">Number Of Employees</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <b-form-input
                  id="numberOfEmployees"
                  v-model="financial.numberOfEmployees"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="freeholdPropertyValue">Freehold Property Value</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="freeholdPropertyValue"
                  v-model="financial.freeholdPropertyValueAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="currentLiabilities">Current Liabilities</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="currentLiabilities"
                  v-model="financial.currentLiabilitiesAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="netAssets">Net Assets</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                 <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="netAssets"
                  v-model="financial.netAssetsAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="researchDevelopment">Research & Development</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="researchDevelopment"
                  v-model="financial.researchAndDevelopmentAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="taxation">Taxation</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="taxation"
                  v-model="financial.taxationAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
              <label for="capitalExpenditureFinancInvest">Capital Expenditure Financ Invest</label>
            </b-col>
            <b-col md="4">
              <b-input-group>
                <template #prepend>
                  <b-input-group-text >{{ currency.code }}</b-input-group-text>
                </template>
                <b-form-input
                  id="capitalExpenditureFinancInvest"
                  v-model="financial.capitalExpenditureFinancInvestAdjusted"
                ></b-form-input>
              </b-input-group>
            </b-col>
          </b-row>

          <b-row class="my-1">
            <b-col md="3">
              <label for="input-none"></label>
            </b-col>
            <b-col md="9">
              <b-button type="submit" variant="success" class="margin-right">
                <font-awesome-icon icon="save" /> Save
              </b-button>
              <b-button type="reset" variant="danger" @click="hideModal">
                <font-awesome-icon icon="times" /> Cancel Changes
              </b-button>
            </b-col>
          </b-row>
          <b-row class="my-1">
            <b-col md="3">
            </b-col>
            <b-col md="9">
              <Feedback ref="saveFeedback"/>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</template>

<script>
import CompanyService from "@/services/Company.service";
import Feedback from '@/components/shared/Feedback.vue';

export default {
  components: {Feedback},
  props: {
    company: Object,
    financial: Object,
    currency: Object,
  },
  data() {
    return {
      months: [
        { id: 1, name: "January" },
        { id: 2, name: "February" },
        { id: 3, name: "March" },
        { id: 4, name: "April" },
        { id: 5, name: "May" },
        { id: 6, name: "June" },
        { id: 7, name: "July" },
        { id: 8, name: "August" },
        { id: 9, name: "September" },
        { id: 10, name: "October" },
        { id: 11, name: "November" },
        { id: 12, name: "December" },
      ],
    };
  },
  methods: {
    showModal() {
      this.$refs["modal"].show();
    },
    hideModal() {
      this.$refs["modal"].hide();
    },

    onSubmit() {
      this.financial.ebitda = this.ebitda;
      this.financial.grossProfitPercent = this.grossProfitPercent;
      CompanyService.saveCompanyFinances(this.financial).then(() => {
        this.$parent.$parent.$parent.$parent.loadData();
        this.hideModal();
      }).catch(error => {
        console.log(error)
        if (error.data == "Duplicate") {
          this.$refs.saveFeedback.showError("Duplicate", "A record already exists for this date");
        } else {
          this.$refs.saveFeedback.showError("Error", "An error has prevented this record saving");
        }
      });
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
  },
  computed: {
    title() {
      if (this.financial == null || this.financial.id > 0) {
        return "Edit Finance";
      } else {
        return "Add Finance";
      }
    },
    grossProfitPercent() {
      if (
        this.financial == null ||
        this.financial.turnover == null ||
        this.financial.turnover == "" ||
        this.financial.turnover == 0
      )
        return 0;

      var grossProfit = this.financial.grossProfit;
      if (grossProfit == null || grossProfit == "") grossProfit = 0;

      var result = ((grossProfit / this.financial.turnover) * 100.0).toFixed(2);
      //this.financial.grossProfitPercent = result;
      return result;
    },
    ebitda() {
      if (
        this.financial == null ||
        this.financial.ebitAdjusted == null ||
        this.financial.ebitAdjusted == "" ||
        this.financial.ebitAdjusted < 0
      )
        return null;

      var depreciation = this.financial.depreciationAdjusted || 0;
      var amortisation = this.financial.amortisationAdjusted || 0;

      var result = (
        parseInt(this.financial.ebitAdjusted) +
        parseInt(depreciation) +
        parseInt(amortisation)
      ).toFixed(2);

      return result;
    },
    years() {
      var d = new Date();
      var year = d.getFullYear();
      var years = [];

      for (var i = year - 99; i <= year + 5; i++) {
        years.unshift(i);
      }

      return years;
    },
  },
};
</script>
